import React from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";

// Regras do donut da Visão Geral (pedido do dono, 21/09/2026: os relatórios
// seguem o mesmo padrão): anel 55/95, % dentro da fatia só a partir de 5%,
// balão com bolinha da cor + nome + valor, legenda em lista embaixo e no máximo
// 10 fatias — o resto vira "Outros (N)" em cinza.
export const DONUT_COLORS = ["#6366f1", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6", "#06b6d4", "#f97316", "#ec4899"];
const COR_OUTROS = "#94a3b8";

export const corDaFatia = (item, i) => (item?.__outros ? COR_OUTROS : DONUT_COLORS[i % DONUT_COLORS.length]);

// `somarTambem`: outras colunas numéricas que a fatia "Outros" também soma
// (ex.: a quantidade de pedidos, que aparece na legenda).
export function agruparOutros(lista, dataKey, n = 10, somarTambem = []) {
  const arr = [...(lista || [])].sort((a, b) => (b[dataKey] || 0) - (a[dataKey] || 0));
  if (arr.length <= n) return arr;
  const resto = arr.slice(n);
  const somar = (k) => resto.reduce((s, it) => s + (Number(it[k]) || 0), 0);
  const soma = somar(dataKey);
  const top = arr.slice(0, n);
  if (soma > 0) {
    const outros = { name: `Outros (${resto.length})`, [dataKey]: soma, __outros: true };
    somarTambem.forEach((k) => { outros[k] = somar(k); });
    top.push(outros);
  }
  return top;
}

function Balao({ active, payload, formatVal }) {
  if (!active || !payload?.[0]) return null;
  const d = payload[0];
  return (
    <div className="bg-popover border rounded-lg shadow-lg px-3 py-2 text-sm">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.payload.fill }} />
        <span className="font-semibold">{d.name}</span>
      </div>
      <p className="text-muted-foreground">{formatVal(d.value, d.payload)}</p>
    </div>
  );
}

function rotuloPct({ cx, cy, midAngle, innerRadius, outerRadius, percent }) {
  if (percent < 0.05) return null;
  const rad = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.5;
  return (
    <text x={cx + r * Math.cos(-midAngle * rad)} y={cy + r * Math.sin(-midAngle * rad)} fill="#fff" textAnchor="middle"
      dominantBaseline="central" className="text-xs font-bold" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.5)" }}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
}

// `data` já agrupado (agruparOutros). `formatVal(valor, item)` escreve o balão.
export function DonutGrafico({ data, dataKey, formatVal, height = 240 }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie data={data} cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={3} cornerRadius={4}
          labelLine={false} label={rotuloPct} dataKey={dataKey} stroke="none">
          {data.map((item, i) => <Cell key={item.name || i} fill={corDaFatia(item, i)} />)}
        </Pie>
        <Tooltip content={<Balao formatVal={formatVal} />} />
      </PieChart>
    </ResponsiveContainer>
  );
}

// Lista embaixo do donut: bolinha, nome, % e o valor (`valueLabel(item)`).
export function DonutLegenda({ data, dataKey, valueLabel }) {
  const total = data.reduce((s, item) => s + (item[dataKey] || 0), 0);
  return (
    <div className="mt-4 space-y-2">
      {data.map((item, i) => (
        <div key={item.name || i} className="flex items-center gap-2.5 text-sm">
          <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: corDaFatia(item, i) }} />
          <span className="flex-1 truncate">{item.name}</span>
          <span className="text-muted-foreground text-xs tabular-nums mr-1">
            {total > 0 ? (((item[dataKey] || 0) / total) * 100).toFixed(0) : 0}%
          </span>
          <span className="font-semibold tabular-nums">{valueLabel(item)}</span>
        </div>
      ))}
    </div>
  );
}
